// Pure helpers for the Watermarks Library page. Same pattern as
// utils/sources.js — extracted so the math is testable without
// mounting the page's Redux + axios stack.

// "Today" cutoff anchored to local midnight so "today" matches what
// the operator sees on their wall clock. Pass a `now` for tests.
function _todayStart(now = new Date()) {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

// Aggregate stats shown above the library table.
//
// `today` counters are anchored on the LOCAL midnight (operator's
// wall clock), not UTC, because the operator-facing question is
// "what shipped today on my schedule." All-time / non-today
// counters use the full job set.
//
// Buckets:
//   inFlight    — encoding / HLS-active / packaging
//   delivered   — shares emailed (delivered_at populated)
//   watched     — recipient watched ≥95% (lifecycle_stage='watched')
//   stalled     — delivered but no recipient activity in >72h
//   failed      — terminal failed
//   completedToday — all jobs whose completed_at is today
export function computeLibraryAggregates(jobs, now = new Date()) {
  const list = Array.isArray(jobs) ? jobs : [];
  const todayStartMs = _todayStart(now);
  const stalledCutoff = now.getTime() - 72 * 60 * 60 * 1000; // 72h
  let inFlight = 0;
  let delivered = 0;
  let watched = 0;
  let stalled = 0;
  let failed = 0;
  let completedToday = 0;
  let deliveredToday = 0;
  let watchedToday = 0;
  for (const j of list) {
    if (!j) continue;
    const stage = j.lifecycle_stage;
    if (
      stage === 'encoding' || stage === 'stream_queued' ||
      stage === 'stream_starting' || stage === 'stream_encoding'
    ) {
      inFlight += 1;
    }
    if (j.delivered_at) delivered += 1;
    if (stage === 'watched') watched += 1;
    if (stage === 'failed') failed += 1;

    // Stalled = delivered but no recipient signal in >72h. Excludes
    // anything that's already "watched" (terminal happy state) or
    // "downloaded" (terminal happy state for download-only flows).
    if (
      j.delivered_at && stage !== 'watched' && stage !== 'downloaded'
      && stage !== 'failed' && stage !== 'cancelled' && stage !== 'expired'
    ) {
      const lastActMs = j.last_activity_at ? new Date(j.last_activity_at).getTime() : null;
      const deliveredMs = new Date(j.delivered_at).getTime();
      const referenceMs = lastActMs || deliveredMs;
      if (referenceMs < stalledCutoff) stalled += 1;
    }

    // Today counters
    if (j.completed_at) {
      const ms = new Date(j.completed_at).getTime();
      if (ms >= todayStartMs) completedToday += 1;
    }
    if (j.delivered_at) {
      const ms = new Date(j.delivered_at).getTime();
      if (ms >= todayStartMs) deliveredToday += 1;
    }
    if (j.last_activity_at && stage === 'watched') {
      const ms = new Date(j.last_activity_at).getTime();
      if (ms >= todayStartMs) watchedToday += 1;
    }
  }
  return {
    total: list.length,
    inFlight, delivered, watched, stalled, failed,
    completedToday, deliveredToday, watchedToday,
  };
}

// Relative-time formatter ("3h ago", "just now", "2d ago"). Returns
// '' for null/undefined timestamps. Used for the Last activity
// column. Anchored to a passed-in `now` for deterministic tests.
export function formatRelativeTime(ts, now = new Date()) {
  if (!ts) return '';
  const then = new Date(ts).getTime();
  const ms = now.getTime() - then;
  if (Number.isNaN(ms)) return '';
  if (ms < 0) return 'in the future';
  const sec = Math.floor(ms / 1000);
  if (sec < 30) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  const yr = Math.floor(day / 365);
  return `${yr}y ago`;
}

// Stages where the lifecycle dot SHOULD pulse (active encoding /
// stream-encoding work). Other "active" stages (watching,
// downloading) are recipient-side and pulsing across many rows
// turns the page into a Christmas tree.
export const PULSING_STAGES = new Set([
  'encoding', 'stream_starting', 'stream_encoding',
]);

export function shouldPulse(lifecycleStage) {
  return PULSING_STAGES.has(lifecycleStage);
}

// Stalled-delivery filter: delivered but no recipient signal in
// >72h. Matches the aggregate-stats `stalled` bucket. Used as a
// status filter option on the Library toolbar.
export function isStalled(job, now = new Date()) {
  if (!job || !job.delivered_at) return false;
  const stage = job.lifecycle_stage;
  if (stage === 'watched' || stage === 'downloaded'
      || stage === 'failed' || stage === 'cancelled' || stage === 'expired') {
    return false;
  }
  const stalledCutoff = now.getTime() - 72 * 60 * 60 * 1000;
  const lastActMs = job.last_activity_at ? new Date(job.last_activity_at).getTime() : null;
  const deliveredMs = new Date(job.delivered_at).getTime();
  const referenceMs = lastActMs || deliveredMs;
  return referenceMs < stalledCutoff;
}

// Aggregate stats for the DCP Library page. Different bucket set
// from Watermarks because DCPs don't have watch sessions or per-
// recipient lifecycle — the unit of "did the destination get
// what they need to play this" is the KDM, not a watch event.
//
// Buckets:
//   total          — all DCPs in scope (excl. archived)
//   inFlight       — CREATING / BUILDING / PACKAGING
//   distributed    — completed AND has at least one delivery share
//                    OR a KDM record (i.e. shipped somewhere)
//   awaitingKdm    — encrypted DCP, completed, has at least one
//                    share, but no KDM yet (operator action needed)
//   stalled        — delivered to a venue but no recipient signal in
//                    >72h (mirrors Watermarks `isStalled` shape)
//   failed         — terminal failed
//   completedToday — completed_at within local-midnight today
export function computeDcpAggregates(dcps, packages, kdms, now = new Date()) {
  const dcpList = Array.isArray(dcps) ? dcps : [];
  const pkgList = Array.isArray(packages) ? packages : [];
  const kdmList = Array.isArray(kdms) ? kdms : [];
  const todayStartMs = _todayStart(now);
  const stalledCutoff = now.getTime() - 72 * 60 * 60 * 1000;

  const pkgByDcp = new Map();
  for (const p of pkgList) {
    if (p && p.source_type === 'dcp' && p.source_job_id) {
      const list = pkgByDcp.get(p.source_job_id) || [];
      list.push(p);
      pkgByDcp.set(p.source_job_id, list);
    }
  }
  const kdmCountByDcp = new Map();
  for (const k of kdmList) {
    if (!k || k.deleted_at) continue;
    const id = k.dcp_job_id;
    if (!id) continue;
    kdmCountByDcp.set(id, (kdmCountByDcp.get(id) || 0) + 1);
  }

  let inFlight = 0;
  let distributed = 0;
  let awaitingKdm = 0;
  let stalled = 0;
  let failed = 0;
  let completedToday = 0;
  for (const d of dcpList) {
    if (!d) continue;
    const status = d.status;
    if (status === 'creating' || status === 'building' || status === 'packaging') {
      inFlight += 1;
    }
    if (status === 'failed') failed += 1;

    const pkgs = pkgByDcp.get(d.id) || [];
    const activeShares = [];
    let lastShareSignal = null;
    for (const p of pkgs) {
      if (p.revoked) continue;
      for (const s of (p.shares || [])) {
        if (s.revoked_at) continue;
        activeShares.push(s);
        const tsCandidates = [s.activity_last_at, s.email_sent_at];
        for (const t of tsCandidates) {
          if (!t) continue;
          const ms = new Date(t).getTime();
          if (lastShareSignal == null || ms > lastShareSignal) {
            lastShareSignal = ms;
          }
        }
      }
    }
    const kdmCount = kdmCountByDcp.get(d.id) || 0;

    if (status === 'completed' && (activeShares.length > 0 || kdmCount > 0)) {
      distributed += 1;
    }

    // "Awaiting KDM" = encrypted DCP, completed, has at least one
    // active share to a venue, but zero KDMs issued.
    if (status === 'completed' && d.encrypt && activeShares.length > 0 && kdmCount === 0) {
      awaitingKdm += 1;
    }

    // Stalled = delivered (has active share with email sent) but no
    // recipient signal in >72h. Excludes already-distributed-with-KDM
    // (operator's done their part) and ENCRYPTED dcps already counted
    // by awaitingKdm — but unencrypted stalls still go here, since
    // they have no KDM concept to be "awaiting."
    if (status === 'completed' && activeShares.length > 0) {
      const reference = lastShareSignal;
      const inAwaitingBucket = d.encrypt && kdmCount === 0;
      if (reference && reference < stalledCutoff && !inAwaitingBucket) {
        stalled += 1;
      }
    }

    if (d.completed_at) {
      const ms = new Date(d.completed_at).getTime();
      if (ms >= todayStartMs) completedToday += 1;
    }
  }
  return {
    total: dcpList.length,
    inFlight, distributed, awaitingKdm, stalled, failed,
    completedToday,
  };
}

// Bulk-ops gating: same threshold + superadmin override pattern
// as Sources. Different localStorage key so toggling on Sources
// doesn't auto-enable on Library and vice versa.
export const LIBRARY_BULK_OPS_THRESHOLD = 10;
export const LIBRARY_BULK_OPS_FORCE_KEY = 'library_bulk_force_visible';

export function shouldShowLibraryBulkOps(jobCount, isSuperadmin, forceVisible) {
  if (jobCount >= LIBRARY_BULK_OPS_THRESHOLD) return true;
  if (isSuperadmin && forceVisible) return true;
  return false;
}

export function isLibraryBulkOpsForced() {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(LIBRARY_BULK_OPS_FORCE_KEY) === '1';
  } catch (_e) {
    return false;
  }
}

export function setLibraryBulkOpsForced(forced) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (forced) window.localStorage.setItem(LIBRARY_BULK_OPS_FORCE_KEY, '1');
    else window.localStorage.removeItem(LIBRARY_BULK_OPS_FORCE_KEY);
  } catch (_e) {
    // fail-open like the other localStorage helpers
  }
}

// KDM-specific helpers for the Distribution page. KDMs have an
// intrinsic time dimension (valid_from / valid_to) that the rest
// of the app doesn't — surface that as the primary signal.

// Per-KDM lifecycle stage (rank-ordered, highest priority wins):
//   revoked  — share/package revoked OR record deleted
//   expired  — past valid_to
//   expiring — within EXPIRING_WINDOW_DAYS of valid_to
//   active   — currently within validity window
//   pending  — record created but not yet completed (not yet active)
export const KDM_EXPIRING_WINDOW_DAYS = 7;

export function kdmExpiryStage(kdm, now = new Date()) {
  if (!kdm) return null;
  if (kdm.deleted_at) return 'revoked';
  if (kdm.status && kdm.status !== 'completed') return 'pending';
  const validTo = kdm.valid_to ? new Date(kdm.valid_to).getTime() : null;
  const validFrom = kdm.valid_from ? new Date(kdm.valid_from).getTime() : null;
  const nowMs = now.getTime();
  if (validTo == null) return 'active';
  if (validTo < nowMs) return 'expired';
  // Inside expiring window — operator action required soon.
  const expiringCutoff = nowMs + KDM_EXPIRING_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  if (validTo <= expiringCutoff) return 'expiring';
  // Future window not yet open.
  if (validFrom != null && validFrom > nowMs) return 'pending';
  return 'active';
}

export function computeKdmAggregates(kdms, now = new Date()) {
  const list = Array.isArray(kdms) ? kdms : [];
  const todayStartMs = _todayStart(now);
  let total = 0;
  let active = 0;
  let expiring = 0;
  let expired = 0;
  let revoked = 0;
  let pending = 0;
  let issuedToday = 0;
  for (const k of list) {
    if (!k) continue;
    total += 1;
    const stage = kdmExpiryStage(k, now);
    if (stage === 'active')   active += 1;
    if (stage === 'expiring') expiring += 1;
    if (stage === 'expired')  expired += 1;
    if (stage === 'revoked')  revoked += 1;
    if (stage === 'pending')  pending += 1;
    if (k.created_at) {
      const ms = new Date(k.created_at).getTime();
      if (ms >= todayStartMs) issuedToday += 1;
    }
  }
  return { total, active, expiring, expired, revoked, pending, issuedToday };
}

// Same threshold-gated pattern for the DCP Library, distinct from
// Watermarks Library so toggling one doesn't accidentally enable
// the other.
export const DCP_BULK_OPS_THRESHOLD = 10;
export const DCP_BULK_OPS_FORCE_KEY = 'dcp_library_bulk_force_visible';

export function shouldShowDcpBulkOps(dcpCount, isSuperadmin, forceVisible) {
  if (dcpCount >= DCP_BULK_OPS_THRESHOLD) return true;
  if (isSuperadmin && forceVisible) return true;
  return false;
}

export function isDcpBulkOpsForced() {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(DCP_BULK_OPS_FORCE_KEY) === '1';
  } catch (_e) {
    return false;
  }
}

export function setDcpBulkOpsForced(forced) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (forced) window.localStorage.setItem(DCP_BULK_OPS_FORCE_KEY, '1');
    else window.localStorage.removeItem(DCP_BULK_OPS_FORCE_KEY);
  } catch (_e) {
    // fail-open
  }
}

// Distribution page bulk-ops gating — distinct localStorage key.
export const DISTRIBUTION_BULK_OPS_THRESHOLD = 10;
export const DISTRIBUTION_BULK_OPS_FORCE_KEY = 'distribution_bulk_force_visible';

export function shouldShowDistributionBulkOps(kdmCount, isSuperadmin, forceVisible) {
  if (kdmCount >= DISTRIBUTION_BULK_OPS_THRESHOLD) return true;
  if (isSuperadmin && forceVisible) return true;
  return false;
}

export function isDistributionBulkOpsForced() {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(DISTRIBUTION_BULK_OPS_FORCE_KEY) === '1';
  } catch (_e) {
    return false;
  }
}

export function setDistributionBulkOpsForced(forced) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (forced) window.localStorage.setItem(DISTRIBUTION_BULK_OPS_FORCE_KEY, '1');
    else window.localStorage.removeItem(DISTRIBUTION_BULK_OPS_FORCE_KEY);
  } catch (_e) {
    // fail-open
  }
}

// Per-DCP "is this row stalled" check used by the status filter.
// `dcp` is a DCPJob row, `packages` and `kdms` are the workspace
// lists. Same shape as Watermarks `isStalled` — delivered + no
// recipient signal in >72h + not already in a happy terminal state.
export function isDcpStalled(dcp, packages, kdms, now = new Date()) {
  if (!dcp || dcp.status !== 'completed') return false;
  const pkgs = (Array.isArray(packages) ? packages : []).filter(
    (p) => p && p.source_type === 'dcp' && p.source_job_id === dcp.id && !p.revoked,
  );
  const kdmCount = (Array.isArray(kdms) ? kdms : [])
    .filter((k) => k && !k.deleted_at && k.dcp_job_id === dcp.id).length;
  const activeShares = [];
  let lastSignal = null;
  for (const p of pkgs) {
    for (const s of (p.shares || [])) {
      if (s.revoked_at) continue;
      activeShares.push(s);
      for (const t of [s.activity_last_at, s.email_sent_at]) {
        if (!t) continue;
        const ms = new Date(t).getTime();
        if (lastSignal == null || ms > lastSignal) lastSignal = ms;
      }
    }
  }
  if (activeShares.length === 0) return false;
  if (kdmCount > 0) return false; // happy terminal — operator's part is done
  const cutoff = now.getTime() - 72 * 60 * 60 * 1000;
  return lastSignal != null && lastSignal < cutoff;
}

// "Awaiting KDM" — encrypted DCP, completed, has at least one
// active share but zero KDMs. Used by the status filter.
export function isDcpAwaitingKdm(dcp, packages, kdms) {
  if (!dcp || dcp.status !== 'completed' || !dcp.encrypt) return false;
  const pkgs = (Array.isArray(packages) ? packages : []).filter(
    (p) => p && p.source_type === 'dcp' && p.source_job_id === dcp.id && !p.revoked,
  );
  const activeShares = pkgs.flatMap((p) => (p.shares || []).filter((s) => !s.revoked_at));
  if (activeShares.length === 0) return false;
  const kdmCount = (Array.isArray(kdms) ? kdms : [])
    .filter((k) => k && !k.deleted_at && k.dcp_job_id === dcp.id).length;
  return kdmCount === 0;
}


// Watch-completion bucketing. Operators don't need "5.03%" — they
// want a fast-read bucket. v10.5.5 buckets:
//   < 10%        → "<10%"
//   10–24%       → "<25%"
//   25–49%       → "<50%"
//   50–74%       → "<75%"
//   75–89%       → "<90%"
//   90%+         → "complete"
// Input is a number 0–100. Null / NaN / negative inputs return "—".
export function formatWatchPercent(pct) {
  if (pct == null || Number.isNaN(pct)) return '—';
  const n = Math.max(0, Math.min(100, Number(pct) || 0));
  if (n >= 90) return 'complete';
  if (n >= 75) return '<90%';
  if (n >= 50) return '<75%';
  if (n >= 25) return '<50%';
  if (n >= 10) return '<25%';
  return '<10%';
}

// MUI color name for the bucket — drives chip color where used.
export function watchPercentColor(pct) {
  if (pct == null || Number.isNaN(pct)) return 'default';
  const n = Number(pct) || 0;
  if (n >= 90) return 'success';
  if (n >= 50) return 'warning';
  return 'default';
}
